import { RefreshCw } from 'lucide-react';

import { Button } from '../../ui/button';
import { CreateSowDialog } from './sowDialogs';
import { AdminCreateTeamModal } from '../AdminCreateTeamModal';
import { AdminEditCompanyModal } from '../AdminCreateCompanyModal';
import { AdminEditUserModal } from '../AdminEditUserModal';
import { AdminToast } from '../AdminToast';
import { CompanyDetailHeader } from './CompanyDetailHeader';
import { CompanyDetailEmptyState } from './CompanyDetailPrimitives';
import { CompanyHistoricalLedgerImportDialog } from './CompanyHistoricalLedgerImportDialog';
import {
    CompanyDetailTabBar,
    CompanyPulseSubscriptionsTab,
    CompanySowsTab,
    CompanyTeamsTab,
    CompanyUsersTab,
} from './CompanyDetailTabPanels';
import { PulseSubscriptionDialog } from './PulseSubscriptionDialog';
import { COMPANY_USERS_PAGE_SIZE, useCompanyDetail } from './useCompanyDetail';

export function CompanyDetail() {
    const detail = useCompanyDetail();

    if (!detail.numericCompanyId) {
        return <CompanyDetailEmptyState message="Invalid ID." />;
    }

    const activeCompanyMemberUserIds = new Set<number>();
    for (const team of detail.teamsQuery.data?.teams ?? []) {
        if (team.isActive === false) {
            continue;
        }
        for (const member of team.members) {
            const userId = Number(member.user.id);
            if (member.membershipActive !== false && Number.isFinite(userId)) {
                activeCompanyMemberUserIds.add(userId);
            }
        }
    }

    return (
        <div className="space-y-6">
            <CompanyDetailHeader
                companyId={detail.companyId}
                companyDisplayName={detail.companyDisplayName}
                companyIsActive={detail.companyQuery.data?.isActive}
                companyHasProAccess={detail.companyHasProAccess}
                companyHasPulseAccess={detail.companyHasPulseAccess}
                onBack={detail.goBackToAdmin}
                onEditCompany={() => detail.setIsEditCompanyOpen(true)}
                isEditDisabled={!detail.companyQuery.data}
                ssoDetailState={detail.ssoDetailState}
                teamsLoaded={detail.teamsQuery.isSuccess}
                isCompanySyncedToAuth0={detail.isCompanySyncedToAuth0}
                isCompanyAuth0Syncing={detail.isCompanyAuth0Syncing}
                canSyncCompanyUsers={detail.canSyncCompanyUsers}
                onSyncCompany={(syncUsers) => void detail.handleSyncCompanyToAuth0(syncUsers)}
                onOpenHistoricalImport={() => detail.setIsHistoricalImportOpen(true)}
            />

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <CompanyDetailTabBar
                    activeTab={detail.activeTab}
                    sowCount={detail.sowRows.length}
                    pulseSubscriptionCount={
                        detail.pulseSubscriptionsQuery.data?.subscriptions.length ?? 0
                    }
                    teamCount={detail.teamsQuery.data?.teams.length ?? 0}
                    userCount={detail.companyUsersQuery.data?.total ?? 0}
                    onSelectTab={detail.selectActiveTab}
                />
                <div className="flex shrink-0 justify-end gap-2">
                    {detail.activeTab === 'sows' && (
                        <Button type="button" onClick={() => detail.setIsCreateSowOpen(true)}>
                            + Add SOW
                        </Button>
                    )}
                    {detail.activeTab === 'pulse-subscriptions' && (
                        <Button type="button" onClick={detail.openCreatePulseSubscription}>
                            + Add Pulse Subscription
                        </Button>
                    )}
                    {detail.activeTab === 'teams' && (
                        <Button type="button" onClick={() => detail.setIsCreateTeamOpen(true)}>
                            + New team
                        </Button>
                    )}
                    {detail.activeTab === 'users' && (
                        <Button
                            type="button"
                            variant="outline"
                            disabled={
                                detail.syncUsersToAuth0.isPending ||
                                detail.syncingUserId !== null ||
                                !detail.canSyncCompanyUsers
                            }
                            title={
                                detail.canSyncCompanyUsers
                                    ? undefined
                                    : 'Requires administrate:users permission'
                            }
                            onClick={() => void detail.handleSyncAllUnsyncedUsers()}
                        >
                            <RefreshCw
                                className={`size-4 ${
                                    detail.syncUsersToAuth0.isPending ? 'animate-spin' : ''
                                }`}
                                aria-hidden
                            />
                            {detail.syncUsersToAuth0.isPending ? 'Syncing...' : 'Sync all unsynced'}
                        </Button>
                    )}
                </div>
            </div>

            {detail.activeTab === 'sows' && (
                <CompanySowsTab rows={detail.sowRows} onRowClick={detail.handleSowRowClick} />
            )}

            {detail.activeTab === 'pulse-subscriptions' && (
                <CompanyPulseSubscriptionsTab
                    subscriptions={detail.pulseSubscriptionsQuery.data?.subscriptions ?? []}
                    isPending={detail.pulseSubscriptionsQuery.isPending}
                    isError={detail.pulseSubscriptionsQuery.isError}
                    onRowClick={detail.openEditPulseSubscription}
                />
            )}

            {detail.activeTab === 'teams' && (
                <CompanyTeamsTab
                    teams={detail.teamsQuery.data?.teams ?? []}
                    expandedTeamIds={detail.expandedTeamIds}
                    inlineTeamEdit={detail.inlineTeamEdit}
                    inlineTeamNameInputRef={detail.inlineTeamNameInputRef}
                    onToggleExpanded={detail.toggleExpandedTeam}
                    onStartInlineEdit={detail.startInlineTeamEdit}
                    onInlineTeamDraftChange={detail.updateInlineTeamDraft}
                    onFinishInlineTeamNameEdit={detail.finishInlineTeamNameEdit}
                    onSetTeamActive={detail.handleSetTeamActive}
                    onSetMembershipActive={detail.handleSetMembershipActive}
                    teamActivePendingTeamId={detail.teamActivePendingTeamId}
                    teamMembershipLocked={detail.teamMembershipLocked}
                    teamMembershipPending={detail.teamMembershipPending}
                />
            )}

            {detail.activeTab === 'users' && (
                <CompanyUsersTab
                    isError={detail.companyUsersQuery.isError}
                    isPending={detail.companyUsersQuery.isPending}
                    data={detail.companyUsersQuery.data}
                    page={detail.usersPage}
                    pageSize={COMPANY_USERS_PAGE_SIZE}
                    searchDraft={detail.usersSearchDraft}
                    onSearchChange={detail.setUsersSearchDraft}
                    onSearchCommit={detail.commitUsersSearch}
                    onPageChange={detail.setUsersPage}
                    onRowClick={detail.setSelectedUser}
                    onSync={detail.handleSyncUser}
                    syncingUserId={detail.syncingUserId}
                    companyAdminStatusByUserId={detail.companyAdminStatusByUserId}
                    isCompanyAdminStatusPending={detail.companyAdminsQuery.isPending}
                    isCompanyAdminStatusError={detail.companyAdminsQuery.isError}
                    activeCompanyMemberUserIds={activeCompanyMemberUserIds}
                    companyAdminPendingUserId={detail.companyAdminPendingUserId}
                    onSetCompanyUserAdmin={(userId, isAdmin) =>
                        void detail.handleSetCompanyUserAdmin(userId, isAdmin)
                    }
                />
            )}

            <CreateSowDialog
                companyId={detail.numericCompanyId}
                open={detail.isCreateSowOpen}
                onClose={() => detail.setIsCreateSowOpen(false)}
                onCreated={() => detail.showSuccessToast('SOW created.')}
                pricingConfigs={detail.pricingQuery.data?.pricingConfigs ?? []}
                pulseSubscriptionTiers={detail.pulseSubscriptionTiersQuery.data?.tiers ?? []}
                teams={(detail.teamsQuery.data?.teams ?? [])
                    .filter((team) => team.isActive !== false)
                    .map((team) => ({ id: Number(team.id), name: team.name }))
                    .filter((team) => Number.isFinite(team.id))}
                teamsLoading={detail.teamsQuery.isPending}
            />
            <CompanyHistoricalLedgerImportDialog
                open={detail.isHistoricalImportOpen}
                onClose={() => detail.setIsHistoricalImportOpen(false)}
                companyId={detail.numericCompanyId}
                companyName={detail.companyDisplayName}
                pricingConfigs={detail.pricingQuery.data?.pricingConfigs ?? []}
                onApplied={(result) => {
                    detail.showSuccessToast('Historical SOW imported.');
                    detail.handleSowRowClick({ id: result.sowId, name: 'Historical SOW' });
                }}
            />
            <PulseSubscriptionDialog
                open={detail.isPulseSubscriptionDialogOpen}
                subscription={detail.selectedPulseSubscription}
                companyName={detail.companyDisplayName}
                teams={(detail.teamsQuery.data?.teams ?? [])
                    .filter((team) => team.isActive !== false)
                    .map((team) => ({ id: Number(team.id), name: team.name }))
                    .filter((team) => Number.isSafeInteger(team.id) && team.id > 0)}
                tiers={detail.pulseSubscriptionTiersQuery.data?.tiers ?? []}
                isSaving={
                    detail.pulseSubscriptionMutations.createSubscription.isPending ||
                    detail.pulseSubscriptionMutations.updateSubscription.isPending
                }
                onClose={detail.closePulseSubscriptionDialog}
                onSave={detail.savePulseSubscription}
            />
            <AdminEditUserModal
                open={detail.selectedUser !== null}
                user={detail.selectedUser}
                onClose={() => detail.setSelectedUser(null)}
                onSaved={(result) =>
                    result.auth0SyncWarning
                        ? detail.showWarningToast(result.auth0SyncWarning)
                        : detail.showSuccessToast('User updated.')
                }
            />
            <AdminCreateTeamModal
                open={detail.isCreateTeamOpen}
                companyId={detail.numericCompanyId}
                onClose={() => detail.setIsCreateTeamOpen(false)}
                onCreated={() => detail.showSuccessToast('Team created.')}
            />
            <AdminEditCompanyModal
                open={detail.isEditCompanyOpen}
                company={
                    detail.companyQuery.data
                        ? {
                              companyId: detail.numericCompanyId,
                              name: detail.companyQuery.data.name,
                              isActive: detail.companyQuery.data.isActive,
                              customizations: detail.companyQuery.data.customizations,
                          }
                        : null
                }
                onClose={() => detail.setIsEditCompanyOpen(false)}
                onSaved={() => detail.showSuccessToast('Company updated.')}
            />
            <AdminToast toast={detail.toast} onClose={detail.dismissToast} />
        </div>
    );
}
